import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useCancelAssistantPlanMutation,
  useMyPartnerAssistantConversationQuery,
} from "@/partner/entities/assistant";
import {
  AssistantMessageKindType,
  AssistantMessageRoleType,
  type MyPartnerAssistantConversationQueryType,
} from "@/types/graphql/partner.generated";
import {
  executePartnerPlan,
  sendPartnerMessage,
} from "../domains/partnerAssistantApi";
import { feedReducer, initialFeedState, type FeedItem } from "./feedReducer";
import { planOutcome } from "./planOutcome";
import { createStreamRunner } from "./streamRunner";
import type {
  DangerReason,
  AssistantCard,
  EntityCard,
  FeedAttachment,
  PartnerAssistantEvent,
  PlanDto,
  PlanStatus,
  PlanStepStatus,
} from "./types";
import { usePageContext } from "./usePageContext";

type Message = NonNullable<
  MyPartnerAssistantConversationQueryType["myPartnerAssistantConversation"]
>["messages"][number];

function toPlanDto(plan: NonNullable<Message["plan"]>): PlanDto {
  return {
    id: plan.id,
    status: plan.status as string as PlanStatus,
    title: plan.title,
    notes: plan.notes,
    sources: plan.sources,
    previews: Array.isArray(plan.previews)
      ? (plan.previews as EntityCard[])
      : [],
    executedAt: (plan.executedAt as string | null | undefined) ?? null,
    steps: plan.steps.map((s) => ({
      index: s.index,
      description: s.description,
      operation: s.operation,
      dangerous: s.dangerous,
      dangerReason:
        (s.dangerReason as string | null | undefined as
          | DangerReason
          | null
          | undefined) ?? null,
      removedFrom: s.removedFrom,
      status: s.status as string as PlanStepStatus,
      error: s.error ?? null,
    })),
  };
}

function toFeedItem(m: Message): FeedItem {
  if (m.role === AssistantMessageRoleType.UserType) {
    return {
      kind: "user",
      id: m.id,
      text: m.text,
      attachments: m.attachments.map((a) => ({ id: a.id, url: a.url })),
    };
  }
  return {
    kind: "assistant",
    id: m.id,
    text: m.text,
    cards: Array.isArray(m.cards) ? (m.cards as AssistantCard[]) : [],
    ask: (m.ask as { question: string; options: string[] } | null) ?? null,
    plan: m.plan ? toPlanDto(m.plan) : null,
    offTopic: m.kind === AssistantMessageKindType.OffTopicType,
    error: m.kind === AssistantMessageKindType.ErrorType,
    pending: false,
  };
}

const toFeedItems = (
  conversation: NonNullable<
    MyPartnerAssistantConversationQueryType["myPartnerAssistantConversation"]
  >
) => conversation.messages.map(toFeedItem);

/**
 * Лента помощника партнёра: восстановление текущего диалога, отправка
 * сообщения и выполнение плана потоком SSE (Task 18), отмена плана.
 */
export function usePartnerAssistant() {
  const { i18n } = useTranslation();
  // resolvedLanguage: ru/en из supportedLngs (language может быть "en-US",
  // а бэкенд принимает только ru/en, иначе отвечает по-русски).
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const page = usePageContext();
  const [state, dispatch] = useReducer(feedReducer, initialFeedState);
  const hydrated = useRef(false);
  const [runner] = useState(createStreamRunner);
  const { data, refetch } = useMyPartnerAssistantConversationQuery({
    fetchPolicy: "network-only",
  });
  const [cancelPlanMutation] = useCancelAssistantPlanMutation();

  useEffect(() => {
    const conversation = data?.myPartnerAssistantConversation;
    if (hydrated.current || !conversation) return;
    hydrated.current = true;
    dispatch({
      type: "hydrate",
      conversationId: conversation.id,
      items: toFeedItems(conversation),
      initial: true,
    });
  }, [data]);

  const onEvent = useCallback(
    (event: PartnerAssistantEvent) => dispatch({ type: "event", event }),
    []
  );

  const send = useCallback(
    async (text: string, attachments: FeedAttachment[]) => {
      // Ответ восстановления, пришедший позже, не должен затереть ход.
      hydrated.current = true;
      dispatch({ type: "sent", id: `local-${Date.now()}`, text, attachments });
      const res = await runner.run(
        (handler, signal) =>
          sendPartnerMessage(
            {
              conversationId: state.conversationId,
              text,
              attachmentIds: attachments.map((a) => a.id),
              page,
              locale,
            },
            handler,
            signal
          ),
        onEvent
      );
      if (!res.aborted && (res.error || !res.finished)) {
        dispatch({ type: "failed" });
      }
    },
    [runner, onEvent, state.conversationId, page, locale]
  );

  const executePlan = useCallback(
    async (planId: string, confirmedSteps: number[]) => {
      hydrated.current = true;
      dispatch({ type: "planStarted", planId });
      const res = await runner.run(
        (handler, signal) =>
          executePartnerPlan(planId, confirmedSteps, locale, handler, signal),
        onEvent
      );
      const outcome = planOutcome(planId, res);
      if (outcome.action) dispatch(outcome.action);
      if (!outcome.refetch) return;
      try {
        const fresh = await refetch();
        const conversation = fresh.data?.myPartnerAssistantConversation;
        if (conversation) {
          dispatch({
            type: "hydrate",
            conversationId: conversation.id,
            items: toFeedItems(conversation),
          });
        }
      } catch {
        // карточка уже в черновике, статус подтянется при следующем открытии
      }
    },
    [runner, onEvent, locale, refetch]
  );

  /** false: отмена не прошла, карточка не меняется. */
  const cancelPlan = useCallback(
    async (planId: string): Promise<boolean> => {
      try {
        await cancelPlanMutation({ variables: { id: planId } });
      } catch {
        return false;
      }
      dispatch({
        type: "event",
        event: { type: "plan_status", planId, status: "CANCELLED" },
      });
      return true;
    },
    [cancelPlanMutation]
  );

  const newConversation = useCallback(() => {
    runner.abort();
    dispatch({ type: "reset" });
  }, [runner]);

  useEffect(() => () => runner.abort(), [runner]);

  return { state, page, send, executePlan, cancelPlan, newConversation };
}
